/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { ModalController } from "../../../model/navigation";
import { InputAction } from "../../../model/enums/InputAction";
import {
  dismissRecordingHighlight,
  focusNextRecordingHighlightAction,
  focusPreviousRecordingHighlightAction,
  selectFocusedRecordingHighlightAction
} from "../../../viewModel/myHub/recordingHighlight";
import type { TMyHubActionType } from "../../../viewModel/myHub/myHubActionTypes";
import type { UnityAppDispatch, UnityRootState } from "../../../viewModel";

/** Modal name constant for the recording highlight popup. */
export const MY_HUB_RECORDING_HIGHLIGHT_MODAL_NAME: string =
  "MY_HUB_RECORDING_HIGHLIGHT";

/**
 * ModalController for the MyHubRecordingHighlight popup.
 *
 * D-pad routing:
 *   NAV_LEFT/RIGHT → move across the action buttons the item affords
 *   NAV_OK         → activate the focused button
 *   NAV_UP/DOWN    → swallowed; the popup has one row of controls
 *   BACK           → dismiss
 *
 * Every handler returns true. Up and Down have nothing to move to, but they are
 * still consumed rather than left unregistered: the popup is raised over an
 * arbitrary screen, and an unhandled press would otherwise scroll the list or
 * carousel behind it while the user believes the popup has input.
 */
export class MyHubRecordingHighlightController extends ModalController {
  private readonly mDispatch: UnityAppDispatch;
  private readonly mGetState: () => UnityRootState;
  private readonly mOnActionSelect: (actionType: TMyHubActionType) => void;

  /**
   * @param dispatch - Store dispatch, for the focus and dismiss actions.
   * @param getState - Store `getState`, read at press time so the controller
   *   never holds a stale focus index.
   * @param onActionSelect - Invoked with the focused action on NAV_OK. Supplied
   *   by the container so the D-pad and pointer paths run the same handler.
   */
  public constructor(
    dispatch: UnityAppDispatch,
    getState: () => UnityRootState,
    onActionSelect: (actionType: TMyHubActionType) => void
  ) {
    super(MY_HUB_RECORDING_HIGHLIGHT_MODAL_NAME);
    this.mDispatch = dispatch;
    this.mGetState = getState;
    this.mOnActionSelect = onActionSelect;
  }

  protected initialize(): void {
    this.registerHandler(InputAction.NAV_LEFT, (): boolean => {
      this.mDispatch(focusPreviousRecordingHighlightAction());
      return true;
    });

    this.registerHandler(InputAction.NAV_RIGHT, (): boolean => {
      this.mDispatch(focusNextRecordingHighlightAction());
      return true;
    });

    this.registerHandler(InputAction.NAV_UP, (): boolean => true);

    this.registerHandler(InputAction.NAV_DOWN, (): boolean => true);

    this.registerHandler(InputAction.NAV_OK, (): boolean => {
      const actionType: TMyHubActionType | undefined =
        selectFocusedRecordingHighlightAction(this.mGetState());
      if (actionType !== undefined) {
        this.mOnActionSelect(actionType);
      }
      return true;
    });

    this.registerHandler(InputAction.BACK, (): boolean => {
      this.mDispatch(dismissRecordingHighlight());
      return true;
    });
  }
}
